import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  TextField,
  Alert,
  CircularProgress,
  useTheme
} from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';

export default function BillingPlans({ onPlanSelect, onBack }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [couponCode, setCouponCode] = useState('');
  const [couponDiscount, setCouponDiscount] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const theme = useTheme();

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('/api/billing/plans');
      const data = await response.json();
      setPlans(data);
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setValidatingCoupon(true);
    setCouponError('');
    
    try {
      const response = await fetch('/api/billing/validate-coupon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCouponDiscount(data);
      } else {
        const error = await response.json();
        setCouponError(error.error);
        setCouponDiscount(null);
      }
    } catch (error) {
      setCouponError('Failed to validate coupon');
      setCouponDiscount(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handlePlanSelect = (plan) => {
    setSelectedPlan(plan);
  };

  const handleContinue = () => {
    if (!selectedPlan) return;
    
    const planData = {
      planId: selectedPlan.id,
      billingCycle,
      couponCode: couponDiscount ? couponCode : null,
      plan: selectedPlan,
      discount: couponDiscount
    };
    
    onPlanSelect(planData);
  };

  const getPrice = (plan) => {
    const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
    return parseFloat(price);
  };

  const getDiscountedPrice = (plan) => {
    const basePrice = getPrice(plan);
    if (!couponDiscount) return basePrice;
    
    if (couponDiscount.discount_type === 'percentage') {
      return basePrice * (1 - couponDiscount.discount_value / 100);
    } else {
      return Math.max(0, basePrice - couponDiscount.discount_value);
    }
  };

  // Calculate final price for selected plan
  const finalPrice = selectedPlan ? getDiscountedPrice(selectedPlan) : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, textAlign: 'center' }}>
        Choose Your Team Plan
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 4, textAlign: 'center', color: 'text.secondary' }}>
        Select a plan that fits your team size and needs
      </Typography>

      {/* Billing Cycle Toggle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', bgcolor: 'background.paper', borderRadius: 2, p: 0.5 }}>
          <Button
            variant={billingCycle === 'monthly' ? 'contained' : 'text'}
            onClick={() => setBillingCycle('monthly')}
            sx={{ borderRadius: 1 }}
          >
            Monthly
          </Button>
          <Button
            variant={billingCycle === 'yearly' ? 'contained' : 'text'}
            onClick={() => setBillingCycle('yearly')}
            sx={{ borderRadius: 1 }}
          >
            Yearly
            <Chip 
              label="Save 17%" 
              size="small" 
              color="success" 
              sx={{ ml: 1, fontSize: '0.7rem' }}
            />
          </Button>
        </Box>
      </Box>

      {/* Plans Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {plans.map((plan) => {
          const isSelected = selectedPlan?.id === plan.id;
          const price = getPrice(plan);
          const discountedPrice = getDiscountedPrice(plan);
          const hasDiscount = couponDiscount && discountedPrice < price;
          
          return (
            <Grid item xs={12} sx={{ width: `${plan.id === 3 ? ('100%') : ('48%')}` }} md={4} key={plan.id}>
              <Card 
                sx={{ 
                  height: '100%',
                  width: '100%',
                  cursor: 'pointer',
                  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
                  transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: theme.shadows[8]
                  }
                }}
                onClick={() => handlePlanSelect(plan)}
              >
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 'bold' }}>
                    {plan.name}
                  </Typography>
                  
                  <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
                    {plan.description}
                  </Typography>
                  
                  <Box sx={{ mb: 3 }}>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {hasDiscount && discountedPrice === 0 ? (
                        <>
                          FREE
                          <Typography component="span" variant="body1" sx={{ ml: 1 }}>
                            /{billingCycle === 'yearly' ? 'year' : 'month'}
                          </Typography>
                        </>
                      ) : (
                        <>
                          ${hasDiscount ? discountedPrice.toFixed(2) : price.toFixed(2)}
                          <Typography component="span" variant="body1" sx={{ ml: 1 }}>
                            /{billingCycle === 'yearly' ? 'year' : 'month'}
                          </Typography>
                        </>
                      )}
                    </Typography>
                    {hasDiscount && discountedPrice > 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', textDecoration: 'line-through' }}>
                        ${price.toFixed(2)}
                      </Typography>
                    )}
                    {hasDiscount && discountedPrice === 0 && (
                      <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 'bold' }}>
                        Originally ${price.toFixed(2)}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ mb: 3, flexGrow: 1 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      Up to {plan.max_coaches} coaches
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                      Up to {plan.max_athletes} athletes
                    </Typography>
                    {plan.max_coaches >= 999999 && (
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <CheckIcon sx={{ fontSize: 16, mr: 1, color: 'success.main' }} />
                        Unlimited coaches & athletes
                      </Typography>
                    )}
                  </Box>
                  
                  <Button
                    variant={isSelected ? 'contained' : 'outlined'}
                    fullWidth
                    size="large"
                  >
                    {isSelected ? 'Selected' : 'Select Plan'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Coupon Code */}
      <Box sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
          Have a coupon code?
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            fullWidth
            placeholder="Enter coupon code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            error={!!couponError}
            helperText={couponError}
          />
          <Button
            variant="outlined"
            onClick={validateCoupon}
            disabled={validatingCoupon || !couponCode.trim()}
          >
            {validatingCoupon ? <CircularProgress size={20} /> : 'Apply'}
          </Button>
        </Box>
        {couponDiscount && (
          <Alert severity="success" sx={{ mt: 1 }}>
            Coupon applied! {couponDiscount.discount_type === 'percentage' 
              ? `${couponDiscount.discount_value}% off` 
              : `$${couponDiscount.discount_value} off`}
          </Alert>
        )}
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          size="large"
          onClick={handleContinue}
          disabled={!selectedPlan}
        >
          {finalPrice === 0 ? 'Create Team (Free)' : 'Continue to Payment'}
        </Button>
      </Box>
    </Box>
  );
} 